import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  RefreshCw, Loader2, HandshakeIcon, Check, X as XIcon, Phone,
  MessageSquare, AlertCircle, Shield, ShieldPlus, Camera, Smartphone, Search,
  Clock, User, Users,
} from 'lucide-react';
import { supabase } from '@/services/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SolicitudesTabs } from '@/components/SolicitudesTabs';

interface HistoryEntry {
  status: string;
  at: string;
  by_id: string;
  by_name: string;
  note?: string;
}

interface OnboardingRequest {
  id: string;
  profile_id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  service_type: 'existing_alarm' | 'new_alarm' | 'cameras' | 'app_only' | 'ble_button' | 'alarm_cameras' | 'other_services';
  notes: string | null;
  status: 'pending' | 'contacted' | 'completed' | 'rejected';
  handled_by: string | null;
  handled_at: string | null;
  history: HistoryEntry[];
  created_at: string;
}

const STATUS_STYLES: Record<OnboardingRequest['status'], string> = {
  pending:   'bg-amber-500/10 text-amber-600',
  contacted: 'bg-blue-500/10 text-blue-600',
  completed: 'bg-green-500/10 text-green-600',
  rejected:  'bg-red-500/10 text-red-600',
};

const STATUS_LABELS: Record<OnboardingRequest['status'], string> = {
  pending:   'Pendiente',
  contacted: 'Contactado',
  completed: 'Completado',
  rejected:  'Rechazado',
};

const SERVICE_META: Record<OnboardingRequest['service_type'], { label: string; Icon: React.ElementType }> = {
  existing_alarm: { label: 'Cliente existente (alarma)',  Icon: Shield },
  new_alarm:      { label: 'Nuevo contrato de alarma',    Icon: ShieldPlus },
  cameras:        { label: 'Cámaras / monitoreo',         Icon: Camera },
  app_only:       { label: 'Solo app',                    Icon: Smartphone },
  ble_button:     { label: 'Botón de pánico físico',      Icon: Shield },
  alarm_cameras:  { label: 'Agregar alarma/cámaras',      Icon: ShieldPlus },
  other_services: { label: 'Otros servicios (guardias, escoltas, etc.)', Icon: Users },
};

export default function AsiOnboardingRequestsPage() {
  const [requests, setRequests] = useState<OnboardingRequest[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [busyId, setBusyId]       = useState<string | null>(null);
  const [filter, setFilter]       = useState<'pending' | 'all'>('pending');
  const [search, setSearch]       = useState('');
  const [svcFilter, setSvcFilter] = useState<OnboardingRequest['service_type'] | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<OnboardingRequest['status'] | 'all'>('all');
  // Expanded history panel per row
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);
  // Inline note input: tracks which row has the note box open and its text
  const [noteFor, setNoteFor]   = useState<{ id: string; status: OnboardingRequest['status'] } | null>(null);
  const [noteText, setNoteText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let q = supabase
        .from('asi_onboarding_requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (filter === 'pending') q = q.in('status', ['pending', 'contacted']);
      const { data, error: selErr } = await q;
      if (selErr) throw selErr;
      setRequests((data ?? []) as OnboardingRequest[]);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? 'Error cargando solicitudes');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const channel = supabase
      .channel('asi-onboarding-requests-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'asi_onboarding_requests' }, () => {
        void load();
      })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [load]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return requests.filter((r) => {
      if (svcFilter !== 'all' && r.service_type !== svcFilter) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!term) return true;
      return (
        r.full_name.toLowerCase().includes(term) ||
        (r.phone ?? '').toLowerCase().includes(term) ||
        (r.email ?? '').toLowerCase().includes(term) ||
        (r.notes ?? '').toLowerCase().includes(term) ||
        SERVICE_META[r.service_type].label.toLowerCase().includes(term) ||
        STATUS_LABELS[r.status].toLowerCase().includes(term)
      );
    });
  }, [requests, search, svcFilter, statusFilter]);

  const updateStatus = async (id: string, status: OnboardingRequest['status'], note?: string) => {
    setBusyId(id);
    try {
      // Get current user's name for the history entry
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id ?? '';
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();
      const byName = (profileData as { full_name?: string } | null)?.full_name ?? 'Operador';

      // Build history entry
      const historyEntry: HistoryEntry = {
        status,
        at: new Date().toISOString(),
        by_id: userId,
        by_name: byName,
        ...(note ? { note } : {}),
      };

      // Get current history to append to
      const req = requests.find((r) => r.id === id);
      const currentHistory: HistoryEntry[] = req?.history ?? [];

      const { error: updErr } = await supabase
        .from('asi_onboarding_requests')
        .update({
          status,
          handled_by: userId,
          handled_at: new Date().toISOString(),
          history: [...currentHistory, historyEntry],
        })
        .eq('id', id);
      if (updErr) throw updErr;

      // NOTE: We intentionally do NOT update registration_source here.
      // "Completado" is an administrative tracking status only.
      // To give the client access to Dispositivos, the operator must:
      //   1. Create the site + panels in Cuentas → [cuenta]
      //   2. Click "Promover a cuenta gestionada" in the Servicios tab
      // This separation prevents the client from seeing an empty Devices tab.

      await load();
    } catch (e: unknown) {
      alert((e as { message?: string })?.message ?? 'Error actualizando solicitud');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <SolicitudesTabs />
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HandshakeIcon className="w-6 h-6" /> Solicitudes de acceso ASI
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Clientes que solicitaron incorporarse a Corporación ASI desde la app móvil.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="h-9 rounded-md border bg-background px-3 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'pending' | 'all')}
          >
            <option value="pending">Activas</option>
            <option value="all">Todas</option>
          </select>
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Search + filters bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar por nombre, teléfono, email, notas…"
            className="w-full h-9 rounded-md border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setSearch('')}
            >
              <XIcon className="w-3 h-3" />
            </button>
          )}
        </div>
        <select
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={svcFilter}
          onChange={(e) => setSvcFilter(e.target.value as typeof svcFilter)}
        >
          <option value="all">Todos los servicios</option>
          {(Object.entries(SERVICE_META) as [OnboardingRequest['service_type'], typeof SERVICE_META[keyof typeof SERVICE_META]][]).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
        >
          <option value="all">Todos los estados</option>
          {(Object.entries(STATUS_LABELS) as [OnboardingRequest['status'], string][]).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {filtered.length === requests.length
              ? `${filter === 'pending' ? 'Activas' : 'Todas'} (${requests.length})`
              : `${filtered.length} de ${requests.length} resultado${requests.length !== 1 ? 's' : ''}`
            }
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {error}
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {requests.length === 0 ? 'No hay solicitudes en esta categoría.' : 'Ningún resultado coincide con los filtros.'}
            </p>
          ) : (
            <div className="space-y-3">
              {filtered.map((r) => {
                const svc = SERVICE_META[r.service_type];
                const SvcIcon = svc.Icon;
                return (
                  <div
                    key={r.id}
                    className="border rounded-lg p-4 flex flex-col gap-3 hover:bg-muted/30 transition"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <SvcIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{r.full_name}</span>
                          <Badge className={STATUS_STYLES[r.status]}>{STATUS_LABELS[r.status]}</Badge>
                          <Badge variant="outline" className="text-xs">{svc.label}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                          {r.phone && (
                            <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-foreground">
                              <Phone className="w-3 h-3" /> {r.phone}
                            </a>
                          )}
                          {r.email && <span>{r.email}</span>}
                        </div>
                        {r.notes && (
                          <p className="text-sm mt-2 italic border-l-2 border-muted-foreground/30 pl-3 flex items-start gap-1">
                            <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-muted-foreground" />
                            "{r.notes}"
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground mt-2">
                          Recibida: {new Date(r.created_at).toLocaleString('es-GT')}
                        </p>
                      </div>
                      {(r.status === 'pending' || r.status === 'contacted') && (
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          {r.status === 'pending' && (
                            <Button size="sm" variant="outline"
                              onClick={() => { setNoteFor({ id: r.id, status: 'contacted' }); setNoteText(''); }}
                              disabled={busyId === r.id}>
                              {busyId === r.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Phone className="w-3 h-3 mr-1" />}
                              Contactado
                            </Button>
                          )}
                          <Button size="sm"
                            onClick={() => { setNoteFor({ id: r.id, status: 'completed' }); setNoteText(''); }}
                            disabled={busyId === r.id}>
                            {busyId === r.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                            Completado
                          </Button>
                          <Button size="sm" variant="outline"
                            onClick={() => { setNoteFor({ id: r.id, status: 'rejected' }); setNoteText(''); }}
                            disabled={busyId === r.id}>
                            <XIcon className="w-3 h-3 mr-1" /> Rechazar
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Inline note + confirm panel */}
                    {noteFor?.id === r.id && (
                      <div className="ml-14 mt-2 p-3 bg-muted/40 rounded-lg border space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">
                          Marcando como <Badge className={`${STATUS_STYLES[noteFor.status]} text-[10px] px-1.5 py-0`}>{STATUS_LABELS[noteFor.status]}</Badge> — agrega una nota (opcional)
                        </p>
                        <textarea
                          className="w-full text-sm bg-background border rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
                          rows={2}
                          placeholder="Ej: Llamé al cliente, confirmó que tiene alarma Hikvision instalada..."
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => { setNoteFor(null); setNoteText(''); }}>
                            Cancelar
                          </Button>
                          <Button size="sm" onClick={() => {
                            void updateStatus(noteFor.id, noteFor.status, noteText.trim() || undefined);
                            setNoteFor(null);
                            setNoteText('');
                          }} disabled={busyId === noteFor.id}>
                            {busyId === noteFor.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                            Confirmar
                          </Button>
                        </div>
                        {noteFor.status === 'completed' && (
                          <p className="text-xs text-muted-foreground border-t pt-2 mt-1">
                            💡 <strong>Siguiente paso:</strong> Ve a <strong>Cuentas → [cuenta del cliente] → Servicios</strong> y agrega sitios/paneles. Cuando todo esté listo, haz clic en <strong>"Promover a cuenta gestionada"</strong> para que el cliente vea sus dispositivos.
                          </p>
                        )}
                      </div>
                    )}

                    {/* History timeline */}
                    {(r.history?.length > 0) && (
                      <div className="ml-14">
                        <button
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
                          onClick={() => setExpandedHistory(expandedHistory === r.id ? null : r.id)}
                        >
                          <Clock className="w-3 h-3" />
                          {r.history.length} cambio{r.history.length !== 1 ? 's' : ''} registrado{r.history.length !== 1 ? 's' : ''}
                          <span className="ml-1">{expandedHistory === r.id ? '▲' : '▼'}</span>
                        </button>
                        {expandedHistory === r.id && (
                          <div className="space-y-2 border-l-2 border-muted pl-3">
                            {r.history.map((h, i) => (
                              <div key={i} className="text-xs">
                                <div className="flex items-center gap-2">
                                  <Badge className={`${STATUS_STYLES[h.status as OnboardingRequest['status']] ?? 'bg-muted text-muted-foreground'} text-[10px] px-1.5 py-0`}>
                                    {STATUS_LABELS[h.status as OnboardingRequest['status']] ?? h.status}
                                  </Badge>
                                  <span className="flex items-center gap-1 text-muted-foreground">
                                    <User className="w-3 h-3" /> {h.by_name}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {new Date(h.at).toLocaleString('es-GT')}
                                  </span>
                                </div>
                                {h.note && <p className="text-muted-foreground mt-0.5 italic pl-1">"{h.note}"</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
